import { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingContactWidget from "@/components/FloatingContactWidget";
import { AlphabetNav } from "@/components/glossar/AlphabetNav";
import { GlossarCard } from "@/components/glossar/GlossarCard";
import { glossarTerms } from "@/utils/glossarData";
import { getGlossarySchema } from "@/utils/seoSchemas";
import { Input } from "@/components/ui/input";
import { Search, BookOpen, Filter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const categoryLabels = {
  bedrohungen: "Cyber-Bedrohungen",
  compliance: "Compliance & Recht",
  sicherheit: "Technische Sicherheit",
  medizin: "Medizin-IT",
  incident: "Incident Response"
};

const Glossar = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Filter terms
  const filteredTerms = useMemo(() => {
    return glossarTerms.filter(term => {
      const matchesSearch = searchTerm === "" || 
        term.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        term.shortDef.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesLetter = !activeLetter || term.name.startsWith(activeLetter);
      const matchesCategory = !selectedCategory || term.category === selectedCategory;
      
      return matchesSearch && matchesLetter && matchesCategory;
    });
  }, [searchTerm, activeLetter, selectedCategory]);

  // Get active letters (letters that have terms)
  const activeLetters = useMemo(() => {
    const letters = new Set(
      glossarTerms
        .filter(term => !selectedCategory || term.category === selectedCategory)
        .map(term => term.name[0].toUpperCase())
    );
    return Array.from(letters).sort();
  }, [selectedCategory]);

  // Group terms by letter
  const termsByLetter = useMemo(() => {
    const grouped: Record<string, typeof glossarTerms> = {};
    filteredTerms.forEach(term => {
      const letter = term.name[0].toUpperCase();
      if (!grouped[letter]) grouped[letter] = [];
      grouped[letter].push(term);
    });
    return grouped;
  }, [filteredTerms]);

  const categories = Object.keys(categoryLabels) as Array<keyof typeof categoryLabels>;

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Cyber-Sicherheit Glossar | 50 Begriffe einfach erklärt</title>
        <meta 
          name="description" 
          content="Umfassendes Cyber-Sicherheit Lexikon für Arztpraxen: 50 Fachbegriffe von Ransomware bis DSGVO verständlich erklärt. Inklusive Praxis-Beispiele und Schutzmaßnahmen." 
        />
        <meta 
          name="keywords" 
          content="Cybersecurity Glossar, IT-Sicherheit Lexikon, DSGVO Begriffe, Ransomware Definition, Phishing erklärt" 
        />
        <link rel="canonical" href="https://praxis-versichert.de/glossar" />
        <script type="application/ld+json">
          {JSON.stringify(getGlossarySchema(
            glossarTerms.map(t => ({ name: t.name, slug: t.slug, shortDef: t.shortDef }))
          ))}
        </script>
      </Helmet>

      <Navigation />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-background to-accent/10 py-16 border-b border-border">
          <div className="container">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <BookOpen className="h-8 w-8 text-primary" />
                <h1 className="text-4xl md:text-5xl font-bold">
                  Cyber-Sicherheit Glossar
                </h1>
              </div>
              <p className="text-xl text-muted-foreground mb-8">
                50 wichtige Fachbegriffe zu Cybersecurity, DSGVO und IT-Sicherheit – 
                verständlich erklärt für Arztpraxen und Kliniken
              </p>
              
              {/* Search */}
              <div className="relative max-w-2xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Begriff suchen (z.B. Ransomware, DSGVO, Firewall)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-14 text-lg border-2 focus:border-primary"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="border-b border-border bg-muted/30 py-6">
          <div className="container">
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              <Filter className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium text-muted-foreground flex-shrink-0">Kategorie:</span>
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(null)}
                className="flex-shrink-0"
              >
                Alle ({glossarTerms.length})
              </Button>
              {categories.map((cat) => {
                const count = glossarTerms.filter(t => t.category === cat).length;
                return (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                    className="flex-shrink-0"
                  >
                    {categoryLabels[cat]} ({count})
                  </Button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Alphabet Navigation */}
        <AlphabetNav
          activeLetters={activeLetters}
          activeLetter={activeLetter}
          onLetterClick={(letter) => setActiveLetter(letter === activeLetter ? null : letter)}
        />

        {/* Terms Grid */}
        <section className="py-12">
          <div className="container">
            {Object.keys(termsByLetter).length === 0 ? (
              <div className="text-center py-16">
                <p className="text-lg text-muted-foreground">
                  Keine Begriffe gefunden. Versuchen Sie eine andere Suche.
                </p>
              </div>
            ) : (
              <div className="space-y-12">
                {Object.entries(termsByLetter)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([letter, terms]) => (
                    <div key={letter} id={letter}>
                      <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary">
                          {letter}
                        </span>
                        <span className="text-muted-foreground text-lg font-normal">
                          {terms.length} {terms.length === 1 ? 'Begriff' : 'Begriffe'}
                        </span>
                      </h2>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {terms.map((term) => (
                          <GlossarCard key={term.slug} term={term} />
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-primary/10 via-background to-accent/10 border-t border-border">
          <div className="container text-center">
            <h2 className="text-3xl font-bold mb-4">
              Fragen zu einem Begriff?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Unsere Cyber-Experten erklären Ihnen gerne jeden Fachbegriff im Detail 
              und beraten Sie zur passenden Cyberversicherung für Ihre Praxis.
            </p>
            <Button size="lg" asChild>
              <a href="/termin-buchen">
                Kostenlose Beratung vereinbaren
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingContactWidget />
    </div>
  );
};

export default Glossar;
